import React, { useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Visibility from '@material-ui/icons/VisibilityOutlined';
import VisibilityOff from '@material-ui/icons/VisibilityOffOutlined';
import * as colors from '../../theme/colors';
import { StyledFormHelper } from '../../components/loginSignup/customFormHelper';
import { createMarkup, validatePasswordRules } from '../../utils/generic';

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: 2,
  },
  error: {
    color: colors.redA400,
    "& > p": {
      marginBottom: 0
    }
  }
}));

const customStyles = makeStyles(theme => ({
  passwordHint: {
    background: '#01426a',
    borderRadius: 4,
    color: colors.white,
    fontSize: 12,
    padding: 20,
    left: '0',
    position: 'relative',
    top: 0,

    [theme.breakpoints.up('md')]: {
      width: 300,
      "&:not(.modal-view)": {
        left: '100%',
        position: 'absolute',
        top: '-45%',
        width: 230,

        "&::before": {
          borderBottom: '20px solid transparent',
          borderRight: '15px solid #01426a',
          borderTop: '20px solid transparent',
          content: "''",
          height: '2px',
          left: '-14px',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)'
        }
      }
    }
  },
  passwordShouldBe: {
    fontWeight: 'bold',
    marginBbottom: 25
  },
  passwordRule: {
    lineHeight: "22px",
    paddingLeft: 35,

    "&::before": {
      border: "1px solid " + colors.grey200,
      borderColor: colors.tealA200,
      borderRadius: "50%",
      content: "''",
      height: 20,
      left: 25,
      position: "absolute",
      width: 20
    },

    "&.validRule": {
      color: colors.tealA200,

      "&::before": {
        borderColor: colors.tealA200,
        background: colors.tealA200,
        content: "''"
      }
    }
  },
  blockRelative: {
    display: "flex",
    maxHeight: 100,
    [theme.breakpoints.down('xs')]: {
      maxHeight: 700,
      display: "block",
    },
  },
  container: {
    display: "flex",
  },
}));

export const PasswordInputAnonymousUser = ({ field, form, label, helperText, content, fromModal, firstName, username, email, ...props }) => {
  const classes = useStyles();
  const customClasses = customStyles();
  const [showPassword, setShowPassword] = useState(false);

  let error = form.errors[field.name];
  let isTouched = form.touched[field.name];

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  let tooltipMessages;
  let validateResult;
  if (content && content.tooltipMessages) {
    tooltipMessages = content.tooltipMessages.reduce((acc, message) => ({
      ...acc,
      [message.messageCode]: message.message,
    }), {});

    validateResult = validatePasswordRules(field.value, email, username, firstName);
  }

  const passwordChanged = value => {
    field.onChange(value);
  }

  return (
    <React.Fragment>
      <div className={customClasses.blockRelative}>
        <div className={classes.container}>
      <TextField
        className={classes.textField}
        {...props}
        fullWidth
        required
        name={field.name}
        error={!!error && isTouched}
        onChange={passwordChanged}
        type={showPassword ? 'text' : 'password'}
        variant="outlined"
        value={field.value}
        inputProps={{
          maxLength: 100,
        }}
        InputProps={{
          classes,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
          placeholder: label,
        }}
      />
      <div className={classes.helperContainer}>
      {error && isTouched
      ? (<StyledFormHelper helperText={error} isError={true} isHtml={true} />)
      : (<StyledFormHelper helperText={helperText} isHtml={true} />)}
      </div>
      </div>
      <Divider style={{ margin: 10 }} />
      <Box className={classes.containerHint}>
      {tooltipMessages && 
        <div className={customClasses.passwordHint + (fromModal ? " modal-view" : "")}>
          <div className={customClasses.passwordShouldBe} dangerouslySetInnerHTML={createMarkup(tooltipMessages.passwordShouldBe)}></div>
          <div>
            <div className={customClasses.passwordRule + (!field.value || validateResult["minValue"] ? "" : " validRule")}>
              <div className="minValue" dangerouslySetInnerHTML={createMarkup(tooltipMessages.atLeast7Characters)}></div>
            </div>
            <div className={customClasses.passwordRule + (!field.value || validateResult["differentThanUserData"] ? "" : " validRule")}>
              <div className="differentThanUserData" dangerouslySetInnerHTML={createMarkup(tooltipMessages.differentThanUserDataLabel)}></div>
            </div>
            <div className={customClasses.passwordRule + (!field.value || validateResult["isCommonPassword"] ? "" : " validRule")}>
              <div className="isCommonPassword" dangerouslySetInnerHTML={createMarkup(tooltipMessages.avoidCommonPasswordsLabel)}></div>
            </div>
          </div>
        </div>
        }
      </Box>
      </div>
    </React.Fragment>
  );
};
